'use client';

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";

type StoredUser = {
  id?: number;
  email?: string;
  name?: string | null;
  lastName?: string | null;
};

type MaterialesLayoutProps = {
  children: ReactNode;
};

export function MaterialesLayout({ children }: MaterialesLayoutProps) {
  const router = useRouter();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState<StoredUser | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = localStorage.getItem("gp_token");
    const userJson = localStorage.getItem("gp_user");
    if (!token) {
      router.replace("/login");
      return;
    }
    setUser(userJson ? (JSON.parse(userJson) as StoredUser) : null);
    setCheckingAuth(false);
  }, [router]);

  const handleLogout = () => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("gp_token");
    localStorage.removeItem("gp_user");
    router.replace("/");
  };

  const displayName = user?.name?.trim()
    ? [user.name, user?.lastName?.trim()].filter(Boolean).join(" ")
    : user?.email ?? "Usuario";

  if (checkingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#C0E58A] text-[#1F2933]">
        <p className="text-sm">Verificando acceso...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#C0E58A] text-[#1F2933] flex flex-col">
      {/* Navbar */}
      <header className="bg-[#9B4BB9] text-white shadow-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6">
          <Link href="/" className="flex items-center gap-3">
            <Image
              src="/images/LogoGP.png"
              alt="Greenpeace en las escuelas"
              width={140}
              height={40}
              priority
            />
          </Link>

          <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
            <Link
              href="/conoce-mas#programa"
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              EL PROGRAMA
            </Link>
            <span className="border-b-2 border-white/90 pb-1">
              MATERIALES EDUCATIVOS
            </span>
            <a
              href="#contacto"
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              CONTACTO
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden text-sm md:inline">
              Bienvenido, <strong>{displayName}</strong>
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-full bg-white/15 px-4 py-2 text-xs font-semibold hover:bg:white/25"
            >
              Cerrar sesión
            </button>
          </div>
        </div>

        {/* Mobile: bienvenida */}
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 pb-4 md:hidden">
          <p className="text-sm">
            Bienvenido, <strong>{displayName}</strong>
          </p>
        </div>
      </header>

      <main className="mx-auto min-w-0 max-w-[110rem] flex-1 overflow-x-visible px-4 py-10 md:overflow-x-visible">
        {children}
      </main>

      {/* Footer común */}
      <footer
        id="contacto"
        className="relative bg-cover bg-center bg-no-repeat text-[#1F2933]"
        style={{
          backgroundColor: "#9ACD32",
          backgroundImage: "linear-gradient(to top, rgba(30,30,30,0.22) 0%, transparent 22%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12 md:items-center">
            <div className="flex flex-col items-start gap-4 md:order-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2933]">
                Contacto
              </h3>
              <div className="h-px w-16 bg-[#1F2933]/40" aria-hidden />
              <p className="text-sm leading-relaxed text-[#1F2933]/95">
                Para contactarte con nosotros escribinos por las siguientes vías de comunicación:
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://wa.me/5491171206317"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </span>
                    +54 9 11 7120 6317
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:escuelas@greenpeace.org.ar"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </span>
                    escuelas@greenpeace.org.ar
                  </a>
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-center gap-4 md:order-1 md:items-start md:justify-center">
              <a
                href="https://www.greenpeace.org/argentina/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2933]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#9ACD32]"
                aria-label="Fundación Greenpeace Argentina (sitio web)"
              >
                <Image
                  src="/images/gp-logo-blanco.svg"
                  alt="Greenpeace Argentina"
                  width={160}
                  height={48}
                  className="h-auto w-36 object-contain md:w-40"
                />
              </a>
              <p className="text-xs text-[#1F2933]/90">
                © 2026 Greenpeace Argentina. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

